import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const experiences = [
  {
    type: 'work',
    year: '2021',
    yearEnd: 'Present',
    title: 'Full Stack Developer',
    organization: 'Cognizant Technology Solutions',
    location: 'Pune, India',
    isCurrent: true,
    description: [
      'Developing and maintaining enterprise web applications using React.js and .NET',
      'Implementing CI/CD pipelines and cloud deployments on Azure',
      'Collaborating with cross-functional teams to deliver high-quality software solutions',
      'Conducting code reviews and mentoring junior developers',
    ],
    tech: ['React.js', '.NET', 'C#', 'Azure', 'Docker', 'CI/CD', 'Node.js'],
  },
  {
    type: 'education',
    year: '2017',
    yearEnd: '2021',
    title: 'B.Tech in Information Technology',
    organization: 'C. V. Raman Global University',
    location: 'Odisha, India',
    isCurrent: false,
    description: [
      'Core Lead at Google Developer Student Club (DSC)',
      'Participated in multiple hackathons and coding competitions',
      'Developed several academic and personal full-stack projects',
    ],
    tech: ['React', 'Node.js', 'Firebase', 'Bootstrap', 'Python', 'SQL'],
  },
];

export default function Experience() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="experience" className="section-padding overflow-hidden" style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(0,242,255,0.1), transparent)',
      }} />

      <div className="container-custom" ref={ref}>
        {/* Header */}
        <div style={{ marginBottom: '5rem' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="section-label mb-4"
          >
            04 / EXPERIENCE
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="heading-primary"
          >
            Journey &amp;{' '}
            <span className="text-gradient">Background</span>
          </motion.h2>
        </div>

        {/* Timeline */}
        <div style={{ position: 'relative', maxWidth: '900px' }}>
          {/* Vertical line */}
          <motion.div
            initial={{ scaleY: 0 }}
            animate={isInView ? { scaleY: 1 } : {}}
            transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              top: '6px',
              bottom: '6px',
              width: '1px',
              background: 'linear-gradient(to bottom, #00f2ff, rgba(0,242,255,0.1))',
              transformOrigin: 'top',
            }}
            className="left-[15px] sm:left-[110px]"
          />

          {experiences.map((exp, i) => (
            <motion.div
              key={exp.title}
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.35 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex flex-col sm:flex-row gap-3 sm:gap-6 mb-10 sm:mb-16 last:mb-0"
            >
              {/* LEFT — Year column (Desktop) */}
              <div className="hidden sm:flex w-[110px] shrink-0 flex-col items-end pr-6 pt-1">
                <span style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 800,
                  fontSize: exp.isCurrent ? '2rem' : '1.5rem',
                  color: '#00f2ff',
                  lineHeight: 1,
                  letterSpacing: '-0.04em',
                  transition: 'color 0.3s',
                }}>
                  {exp.year}
                </span>
                <span className="text-mono" style={{
                  fontSize: '0.65rem', color: '#849495', letterSpacing: '0.06em',
                  marginTop: '0.25rem',
                }}>
                  — {exp.yearEnd}
                </span>
              </div>

              {/* Timeline dot */}
              <div className={`absolute z-10 ${
                exp.isCurrent 
                  ? 'left-[8px] sm:left-[103px] top-[6px] sm:top-[8px]' 
                  : 'left-[10px] sm:left-[105px] top-[8px] sm:top-[10px]'
              }`}>
                {exp.isCurrent ? (
                  <motion.div
                    animate={{ boxShadow: ['0 0 0 0 rgba(0,242,255,0.4)', '0 0 0 8px rgba(0,242,255,0)', '0 0 0 0 rgba(0,242,255,0.4)'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                    style={{
                      width: 14, height: 14, borderRadius: '50%',
                      background: '#00f2ff',
                      border: '2px solid #080f17',
                    }}
                  />
                ) : (
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: '#2e353f',
                    border: '2px solid #849495',
                  }} />
                )}
              </div>

              {/* RIGHT — Content card */}
              <div className="flex-1 pl-8 sm:pl-6 w-full">
                {/* Year display for Mobile (< sm) */}
                <div className="flex sm:hidden items-center gap-2 mb-2 pt-0.5">
                  <span className={`font-sora font-extrabold ${exp.isCurrent ? 'text-xl text-[#00f2ff]' : 'text-lg text-[#dce3f0]'}`}>
                    {exp.year}
                  </span>
                  <span className="text-mono text-xs text-[#849495]">
                    — {exp.yearEnd}
                  </span>
                </div>

                <div
                  className="card p-5 sm:p-8"
                  style={{
                    borderColor: exp.isCurrent ? 'rgba(0,242,255,0.15)' : 'rgba(255,255,255,0.06)',
                    boxShadow: exp.isCurrent ? '0 0 30px rgba(0,242,255,0.05)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(0,242,255,0.2)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.4), 0 0 20px rgba(0,242,255,0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = exp.isCurrent ? 'rgba(0,242,255,0.15)' : 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.boxShadow = exp.isCurrent ? '0 0 30px rgba(0,242,255,0.05)' : 'none';
                  }}
                >
                  {/* Type badge */}
                  <div className="text-mono" style={{
                    fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: exp.type === 'work' ? '#00f2ff' : '#a78bfa',
                    marginBottom: '0.75rem',
                  }}>
                    {exp.type === 'work' ? '// Work Experience' : '// Education'}
                    {exp.isCurrent && <span style={{ marginLeft: '0.75rem', color: '#6fe068' }}>● Current</span>}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg sm:text-xl font-bold font-sora text-[#dce3f0] tracking-tight mb-1">
                    {exp.title}
                  </h3>

                  {/* Org + location */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-4">
                    <span style={{ color: '#b9cacb', fontWeight: 500, fontSize: '0.95rem' }}>
                      {exp.organization}
                    </span>
                    <span style={{ color: '#849495', fontSize: '0.85rem' }}>
                      · {exp.location}
                    </span>
                  </div>

                  {/* Bullet points */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {exp.description.map((point, j) => (
                      <li key={j} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                        <span style={{ color: '#00f2ff', marginTop: '0.35rem', flexShrink: 0, fontSize: '0.5rem' }}>◆</span>
                        <span style={{ color: '#849495', fontSize: '0.9rem', lineHeight: 1.6 }}>{point}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Tech pills */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {exp.tech.map((t) => (
                      <span key={t} className="tech-pill">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
      }} />
    </section>
  );
}
